import { useEffect, useRef, useState } from "react";
import { TabContent, TabList, TabTrigger, Tabs } from "@/components/ui";
import { askCount } from "@/api/types/studio";
import { useStudioSummary } from "@/api/hooks/useStudio";
import { useAuth } from "@/api/hooks/useAuth";
import { Thread } from "./Thread";
import { Composer } from "./Composer";
import { AsksBoard } from "./AsksBoard";

const LAST_VISIT_KEY = "studio_last_visit_at";

function readLastVisit(): string | null {
  try {
    return window.localStorage.getItem(LAST_VISIT_KEY);
  } catch {
    return null;
  }
}

function writeLastVisit(value: string) {
  try {
    window.localStorage.setItem(LAST_VISIT_KEY, value);
  } catch {
    // Private mode / storage disabled — the dot just won't persist.
  }
}

/**
 * The page fills the app shell's scroll container (`<main>`, the nearest
 * fixed-height ancestor) exactly and manages its own internal scrolling, so
 * the composer stays pinned at thumb reach and nothing scrolls horizontally
 * at 390px. Measuring the immediate parent was wrong: the shell wraps
 * <Outlet/> in an auto-height div, so that measurement fed back the content's
 * own height and left the thread at half the phone screen.
 */
function useShellHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = el.closest("main") ?? el.parentElement;
    if (!container) return;
    const update = () => {
      // Subtract anything the shell renders above us inside <main>.
      const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
      setHeight(Math.max(0, container.clientHeight - offset));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return { ref, height };
}

/**
 * Studio — the in-app channel to the Fox & Owl assistant, plus (for Will)
 * the approval board. Two tabs: Thread and Asks.
 */
export function StudioPage() {
  const { ref, height } = useShellHeight<HTMLDivElement>();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const { data: summary } = useStudioSummary();

  const [unread, setUnread] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Judge "has the assistant said anything since my last visit" once per
    // mount, off the summary's last_assistant_at, then record the visit.
    if (!summary || initializedRef.current) return;
    initializedRef.current = true;
    const last = summary.last_assistant_at;
    const stored = readLastVisit();
    if (last && (!stored || stored < last)) setUnread(true);
    writeLastVisit(last ?? new Date().toISOString());
  }, [summary]);

  const heldForWill = askCount(summary, "held_for_will");

  return (
    <div
      ref={ref}
      style={{ height }}
      className="flex flex-col overflow-hidden px-3 pb-0 pt-4 sm:px-6 sm:pt-6 lg:px-8"
      data-testid="studio-page"
    >
      <h1 className="font-display text-2xl font-bold text-text-primary">Studio</h1>

      <Tabs defaultValue="thread" className="mt-3 flex min-h-0 flex-1 flex-col">
        <TabList className="shrink-0">
          <TabTrigger value="thread" className="gap-1.5">
            Thread
            {unread && (
              <span
                aria-label="New assistant messages since your last visit"
                className="inline-block h-2 w-2 rounded-full bg-info"
                data-testid="studio-unread-dot"
              />
            )}
          </TabTrigger>
          <TabTrigger value="asks" className="gap-1.5">
            Asks
            {isAdmin && heldForWill > 0 && (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-copper px-1.5 text-[11px] font-bold leading-5 text-white" data-testid="studio-asks-badge">
                {heldForWill}
              </span>
            )}
          </TabTrigger>
        </TabList>

        <TabContent value="thread" className="mt-3 min-h-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1">
              <Thread />
            </div>
            <Composer />
          </div>
        </TabContent>

        <TabContent value="asks" className="mt-3 min-h-0 flex-1 overflow-hidden">
          <AsksBoard />
        </TabContent>
      </Tabs>
    </div>
  );
}
